import type { OpenOptions } from '../types';

/**
 * 与 SDK 自有 URL query 冲突的保留 key：
 * - 任意 `daji_` 前缀（内置白名单）
 * - `sdk`（版本号）
 * - `tenant` / `domain` / `source` / `ios`（composeBuyerUrl 的 ctxParams）
 *
 * 用户传 `extra` 时命中保留字将被丢弃并 console.warn（严格 + 显式告知策略）。
 */
const RESERVED_PREFIX = 'daji_';
const RESERVED_KEYS = new Set(['sdk', 'tenant', 'domain', 'source', 'ios']);

export function mergeExtraSafely(
  extra: Record<string, string> | undefined,
  builtInKeys: string[] = [],
): Record<string, string> {
  if (!extra) return {};
  const safe: Record<string, string> = {};
  const builtIn = new Set(builtInKeys);
  for (const [k, v] of Object.entries(extra)) {
    if (!k || k.startsWith(RESERVED_PREFIX) || RESERVED_KEYS.has(k) || builtIn.has(k)) {
      console.warn(`[DajiCS] extra key "${k}" is reserved by SDK; ignored.`);
      continue;
    }
    safe[k] = String(v ?? '');
  }
  return safe;
}

/**
 * 把 OpenOptions 序列化成访客页 daji_* query string。
 * host 由调用方注入（H5 端传 location.host，小程序端传 ''）。
 * 零 DOM 依赖。
 *
 * 安全注意：token 以明文出现在 URL query（daji_token=xxx），
 * 会被浏览器历史 / Referrer / 服务端 access-log 记录。
 * 生产环境应迁移到 postMessage 下发 token。
 */
export function buildQuery(opts: OpenOptions, sdkVersion: string, host: string): string {
  const params: Record<string, string> = {
    daji_userId: String(opts.userId ?? ''),
    daji_userName: String(opts.userName ?? ''),
    daji_userType: String(opts.userType ?? ''),
    daji_language: String(opts.language ?? ''),
    daji_priceType: String(opts.priceType ?? ''),
    daji_supplierId: String(opts.supplierId ?? ''),
    daji_host: host,
    sdk: sdkVersion,
  };
  const safeExtra = mergeExtraSafely(opts.extra, Object.keys(params));
  const merged: Record<string, string> = { ...params, ...safeExtra };
  return Object.entries(merged)
    .filter(([, v]) => v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

export function buildQueryWithToken(opts: OpenOptions, sdkVersion: string, host: string): string {
  const base = buildQuery(opts, sdkVersion, host);
  const token = String(opts.token ?? '');
  if (!token) return base;
  const tokenQs = `${encodeURIComponent('daji_token')}=${encodeURIComponent(token)}`;
  return [base, tokenQs].filter(Boolean).join('&');
}
